import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { OnboardingStatus } from '../types/onboarding';
import apiService from '../services/api';
import { useAuth } from './AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

interface OnboardingContextType {
  status: OnboardingStatus | null;
  isLoading: boolean;
  shouldShowOnboarding: boolean;
  showTour: boolean;
  currentStep: string;
  updateStep: (step: string) => Promise<void>;
  completeOnboarding: () => Promise<void>;
  dismissOnboarding: () => Promise<void>;
  resetOnboarding: () => Promise<void>;
  startTour: () => Promise<void>;
  closeTour: () => void;
  refreshStatus: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [showTour, setShowTour] = useState(false);

  // Fetch onboarding status
  const { data: statusResponse, isLoading, refetch } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: async () => {
      const response = await apiService.getOnboardingStatus();
      return response.success ? response.data : null;
    },
    enabled: isAuthenticated && !!user,
    staleTime: 30 * 1000, // 30 seconds
    retry: 1,
  });

  const status = statusResponse || null;

  // Update step mutation
  const updateStepMutation = useMutation({
    mutationFn: async (step: string) => {
      const response = await apiService.updateOnboardingStep(step);
      if (!response.success) {
        throw new Error(response.message || 'Failed to update onboarding step');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-status'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update onboarding step');
    },
  });

  // Complete onboarding mutation
  const completeOnboardingMutation = useMutation({
    mutationFn: async () => {
      const response = await apiService.completeOnboarding();
      if (!response.success) {
        throw new Error(response.message || 'Failed to complete onboarding');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-status'] });
      toast.success('Welcome to WorkOrder HQ! 🎉');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to complete onboarding');
    },
  });

  // Dismiss onboarding mutation
  const dismissOnboardingMutation = useMutation({
    mutationFn: async () => {
      const response = await apiService.dismissOnboarding();
      if (!response.success) {
        throw new Error(response.message || 'Failed to dismiss onboarding');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-status'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to dismiss onboarding');
    },
  });

  // Reset onboarding mutation
  const resetOnboardingMutation = useMutation({
    mutationFn: async () => {
      const response = await apiService.resetOnboarding();
      if (!response.success) {
        throw new Error(response.message || 'Failed to reset onboarding');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-status'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reset onboarding');
    },
  });

  // Determine if onboarding should be shown
  const shouldShowOnboarding = useMemo(() => {
    if (!status) return false;
    return !status.onboardingCompleted && !status.onboardingDismissed;
  }, [status]);

  // Get current step
  const currentStep = useMemo(() => {
    return status?.currentStep || 'welcome';
  }, [status]);

  // Methods
  const updateStep = async (step: string) => {
    await updateStepMutation.mutateAsync(step);
  };

  const completeOnboarding = async () => {
    await completeOnboardingMutation.mutateAsync();
  };

  const dismissOnboarding = async () => {
    await dismissOnboardingMutation.mutateAsync();
  };

  const resetOnboarding = async () => {
    await resetOnboardingMutation.mutateAsync();
  };

  const startTour = async () => {
    await updateStepMutation.mutateAsync('tour');
    setShowTour(true);
  };

  const closeTour = () => {
    setShowTour(false);
  };

  const refreshStatus = async () => {
    await refetch();
  };

  const value: OnboardingContextType = {
    status,
    isLoading,
    shouldShowOnboarding,
    showTour,
    currentStep,
    updateStep,
    completeOnboarding,
    dismissOnboarding,
    resetOnboarding,
    startTour,
    closeTour,
    refreshStatus,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

